import { network } from "../config";
import {
  FlexPassFilteres,
  FlexPassParamsAvailable,
  FlexPassSubscription,
  NewFlexPass,
} from "../types/FlexPassTypes";
import TypeDefaultResponse from "../types/TypeDefaultResponse";
import { SpecialistData, TypeSpecialistFilter } from "../types/TypeSpecialist";
import { TypeUpdatedUserInfo, TypeUserData } from "../types/TypeUsers";
import url from "../utils/formApiUrl";
import customKy from "./kyExtend";

const { flexpass } = network;

const baseURL = process.env.REACT_APP_HTTP_CONNECTION_STRING + "flexpass/";

const getAuthHeader = (token: string) => {
  return { Authorization: `Bearer ${token}` };
};

export default class FlexPassApi {
  static async createFlexPassSubscription(
    token: string,
    newSubscription: NewFlexPass
  ): Promise<
    TypeDefaultResponse & {
      subscription?: FlexPassSubscription;
    }
  > {
    return await customKy
      .post(url(baseURL, flexpass.createFlexPassSubscription), {
        headers: getAuthHeader(token),
        json: { newSubscription },
        timeout: 30000,
      })
      .then((res) => res.json());
  }

  static async getFilteredFlexPassSubscriptions(
    token: string,
    filters: FlexPassFilteres
  ): Promise<
    TypeDefaultResponse & {
      subscriptions?: FlexPassSubscription[];
      paramsAvailable: FlexPassParamsAvailable;
    }
  > {
    return await customKy
      .post(url(baseURL, flexpass.getFilteredFlexPassSubscriptions), {
        headers: getAuthHeader(token),
        json: { filters },
        timeout: 30000,
      })
      .then((res) => res.json());
  }

  static async inviteSpecToFlexPassSubscription(
    token: string,
    subscriptionId: string,
    specialistIds: string[]
  ): Promise<
    TypeDefaultResponse & {
      successInvites?: (TypeDefaultResponse & {
        user?: Pick<
          TypeUserData,
          "name" | "surname" | "shortId" | "avatar" | "avatarS3" | "_id"
        >;
      })[];
      failedInvites?: (TypeDefaultResponse & {
        user?: Pick<
          TypeUserData,
          "name" | "surname" | "shortId" | "avatar" | "avatarS3" | "_id"
        >;
      })[];
      subscription: FlexPassSubscription;
    }
  > {
    return await customKy
      .post(
        url(baseURL, flexpass.inviteSpecToFlexPassSubscription, {
          id: subscriptionId,
        }),
        {
          headers: getAuthHeader(token),
          json: { specialistIds },
          timeout: 30000,
        }
      )
      .then((res) => res.json());
  }

  static async getPersonalFlexPassSubscriptions(token: string): Promise<
    TypeDefaultResponse & {
      subscriptions?: FlexPassSubscription[];
    }
  > {
    return await customKy
      .get(url(baseURL, flexpass.getPersonalFlexPassSubscriptions), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async getFilteredSubscriptionSpecialists(
    token: string,
    filters: TypeSpecialistFilter,
    subscriptionId: string,
    alreadyFetched?: string[]
  ): Promise<
    TypeDefaultResponse & {
      users?: TypeUserData[] | null[];
      paramsAvailable?: {
        genders?: string[];
        priceRange: { min: number; max: number };
        ageRange: { min: number; max: number };
        yearsOfExperienceRange: { min: number; max: number };
        totalAmount: number;
      };
    }
  > {
    return await customKy
      .post(url(baseURL, flexpass.getFilteredSubscriptionSpecialists), {
        headers: getAuthHeader(token),
        json: { filters, alreadyFetched, subscriptionId },
        timeout: 30000,
      })
      .then((res) => res.json());
  }

  static async getFlexPassParticipants(
    token: string,
    subscriptionId: string
  ): Promise<
    TypeDefaultResponse & {
      participants?: {
        clients: Pick<
          TypeUserData,
          "_id" | "avatar" | "avatarS3" | "name" | "surname" | "shortId" | "email"
        >[];
        specialists: (Pick<
          TypeUserData,
          "_id" | "avatar" | "avatarS3" | "name" | "surname" | "shortId" | "email"
        > & { specialistId: string })[];
      };
    }
  > {
    return await customKy
      .get(
        url(baseURL, flexpass.getFlexPassParticipants, { id: subscriptionId }),
        {
          headers: getAuthHeader(token),
          timeout: 30000,
        }
      )
      .then((res) => res.json());
  }

  static async kickFlexPassSpecialist(
    token: string,
    specialistId: string,
    subscriptionId: string
  ): Promise<TypeDefaultResponse> {
    return await customKy
      .post(
        url(baseURL, flexpass.kickFlexPassSpecialist, {
          id: subscriptionId,
          specId: specialistId,
        }),
        {
          headers: getAuthHeader(token),
          timeout: 30000,
        }
      )
      .then((res) => res.json());
  }

  static async updateFlexPassSubscription(
    token: string,
    updateData: TypeUpdatedUserInfo
  ): Promise<
    TypeDefaultResponse & {
      subscription?: FlexPassSubscription;
    }
  > {
    return await customKy
      .patch(
        url(baseURL, flexpass.updateFlexPassSubscription, {
          id: updateData._id,
        }),
        {
          headers: getAuthHeader(token),
          timeout: 30000,
          json: { updateData },
        }
      )
      .then((res) => res.json());
  }
  static async deleteFlexPassSubscription(
    token: string,
    subscriptionId: string
  ): Promise<
    TypeDefaultResponse & {
      subscription?: FlexPassSubscription;
    }
  > {
    return await customKy
      .delete(
        url(baseURL, flexpass.deleteFlexPassSubscription, {
          id: subscriptionId,
        }),
        {
          headers: getAuthHeader(token),
          timeout: 30000,
        }
      )
      .then((res) => res.json());
  }

  static async getSpecialistsFitForFlexPass(
    token: string,
    subscriptionId: string,
    filters: { page: number; limit: number; name?: string }
  ): Promise<
    TypeDefaultResponse & {
      specialists?: (Omit<TypeUserData, "specialistIds"> & {
        specialistIds: SpecialistData;
        payedOutSessionsCount: number;
      })[];
    }
  > {
    return await customKy
      .post(
        url(baseURL, flexpass.getSpecialistsFitForFlexPass, {
          id: subscriptionId,
        }),
        {
          headers: getAuthHeader(token),
          json: { filters },
          timeout: 30000,
        }
      )
      .then((res) => res.json());
  }
}
